import { GridCellProps, GridColumnProps, GridFilterCellProps } from '@progress/kendo-react-grid'
import {
  CountryComplianceDtoKey,
  CountryComplianceTableDto,
} from 'api/Clients/Types/CountryComplianceDto'
import { ExternalRiskLevelType } from 'api/Clients/Types/ExternalRiskLevelType'
import { InternalRiskLevelType } from 'api/Clients/Types/InternalRiskLevelType'
import { MrloAnalysisType } from 'api/Clients/Types/MrloAnalysisType'
import { inputFilterCell, multiSelectFilterCell } from 'components/Filter/FilterCell'
import { useAppSelector } from 'store'
import { getEnumEntries } from 'utils/getEnumEntries'

import { RoleTag } from '../../Administration/Users/RoleTag'

const TickmillCompanyFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  return multiSelectFilterCell({
    items: tickmillCompanies.map((x) => ({ id: x.id, value: x.id, label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const ExternalRiskLevelFilter = multiSelectFilterCell({
  items: getEnumEntries(ExternalRiskLevelType).map(([key, value]) => ({
    id: value,
    value,
    label: key,
  })),
  options: {
    operator: '==',
  },
})

const InternalRiskLevelFilter = multiSelectFilterCell({
  items: getEnumEntries(InternalRiskLevelType).map(([key, value]) => ({
    id: value,
    value,
    label: key,
  })),
  options: {
    operator: '==',
  },
})

const MRLOAnalysisFilter = multiSelectFilterCell({
  items: getEnumEntries(MrloAnalysisType).map(([key, value]) => ({ id: value, value, label: key })),
  options: {
    operator: '==',
  },
})

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const countryRiskColumns: GridColumn[] = [
  {
    title: 'Entity',
    field: CountryComplianceDtoKey.tickmillCompany,
    filterCell: TickmillCompanyFilterCell,
    show: true,
    width: 200,
    minResizableWidth: 200,
  },
  {
    title: 'Country',
    field: CountryComplianceDtoKey.country,
    filterCell: inputFilterCell({
      options: {
        operator: 'like',
      },
    }),
    cell: (gridCellProps: GridCellProps) => (
      <td className='columns__content'>
        {(gridCellProps.dataItem as CountryComplianceTableDto).country}
      </td>
    ),
    show: true,
    width: 200,
    minResizableWidth: 200,
  },
  {
    title: 'Score',
    field: CountryComplianceDtoKey.score,
    filterCell: inputFilterCell({
      options: {
        operator: '==',
      },
    }),
    className: 'table-columns__number',
    show: true,
    width: 100,
    minResizableWidth: 100,
  },
  {
    title: 'External Risk Level',
    field: CountryComplianceDtoKey.externalRiskLevel,
    filterCell: ExternalRiskLevelFilter,
    className: 'table-columns__content',
    cell: (gridCellProps: GridCellProps): JSX.Element => {
      const item: CountryComplianceTableDto = gridCellProps.dataItem
      if (!item.externalRiskLevel) {
        return <td />
      }
      return (
        <td>
          <RoleTag
            key={item.externalRiskLevel}
            role={item.externalRiskLevel}
            index={item.externalRiskLevel}
          />
        </td>
      )
    },
    show: true,
    width: 160,
    minResizableWidth: 160,
  },
  {
    title: 'Internal Risk Level',
    field: CountryComplianceDtoKey.internalRiskLevel,
    filterCell: InternalRiskLevelFilter,
    className: 'table-columns__content',
    cell: (gridCellProps: GridCellProps): JSX.Element => {
      const item: CountryComplianceTableDto = gridCellProps.dataItem
      if (!item.internalRiskLevel) {
        return <td />
      }
      return (
        <td>
          <RoleTag
            key={item.internalRiskLevel}
            role={item.internalRiskLevel}
            index={item.internalRiskLevel}
          />
        </td>
      )
    },
    show: true,
    width: 160,
    minResizableWidth: 160,
  },
  {
    title: 'MLRO Analysis',
    field: CountryComplianceDtoKey.mRLOAnalysis,
    filterCell: MRLOAnalysisFilter,
    cell: (gridCellProps: GridCellProps) => (
      <td className='table-columns__content'>
        {(gridCellProps.dataItem as CountryComplianceTableDto).countryComplianceMrloAnalyses
          .split(',')
          .map((mrl, i) => (
            <p key={i}>{mrl}</p>
          ))}
      </td>
    ),
    show: true,
    width: 160,
    minResizableWidth: 160,
  },
  {
    title: 'Comment',
    field: CountryComplianceDtoKey.comment,
    filterCell: inputFilterCell({
      options: {
        operator: 'like',
      },
    }),
    className: 'table-columns__content',
    show: true,
    width: 180,
    minResizableWidth: 180,
  },
]
